import styled from 'styled-components';
import { Dialog, Icon } from '@material-ui/core';
import { useGetVehicleByIdQuery } from '../../../../services/api/upload-vehicle';
import { Field, Form, useFormState } from 'react-final-form';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useMemo, useRef } from 'react';
import arrayMutators from 'final-form-arrays';
import {
  closeCarInfoDialog,
  closeInitialPictureDialog,
  closeVinDialog,
  openQuestionsDialog,
  useInitialImageDialogOpened
} from '../../../../services/listerUploadVehicle/v2';
import { CancelAction, SubmitAction } from './ui';
import { useGetObdImagesQuery, useSaveObdImagesMutation } from 'services/api/vehicle-sections';
import { FieldArray, useFieldArray } from 'react-final-form-arrays';
import { SectionImage } from 'services/api/vehicle-sections/types';
import { NoAutoSavePhoto } from 'pages/ListerVehicleUploadV2/components/photos/NoAutoSaveImage';
import { FORM_ERROR } from 'final-form';
import { useDeviceInfo } from 'hooks/useDeviceInfo';
import { LoadingDialog } from 'components/LoadingDialog';
import { closeLoadingModal, openLoadingModal } from 'services/ui/uiSlice';
import { resizeV2 } from 'utils/image';

const StyledDialog = styled(Dialog)`
  .create-vehicle-info-paper {
    margin: auto 13px;
    box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
    border-radius: 20px;
    width: 100%;
  }
`;

const Wrapper = styled.form`
  display: flex;
  gap: 24px;
  flex-direction: column;
  padding: 26px 24px 24px;
  position: relative;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.div`
  font-size: 22px;
  font-weight: 900;
  line-height: 25.78px;
  color: #000;
`;

const CloseIcon = styled(Icon)`
  width: 24px;
  height: 24px;
`;

const ActionsWrapper = styled.div`
  display: flex;
  gap: 19px;
`;

const ErrorMessage = styled.div`
  color: #d01c2e;
  font-size: 12px;
  height: 12px;
`;

const hasImage = (image: SectionImage) => {
  return image.url || image.file;
};

export const InitialImageUploadDialog = () => {
  const { vehicleId, type } = useInitialImageDialogOpened();

  const { data: vehicle } = useGetVehicleByIdQuery(vehicleId!, { skip: !vehicleId });
  const { data: images } = useGetObdImagesQuery(vehicleId!, { skip: !vehicleId });
  const [saveObdImages] = useSaveObdImagesMutation();

  const submitCounter = useRef<number>(0);

  const history = useHistory();
  const dispatch = useDispatch();

  const close = () => {
    dispatch(closeCarInfoDialog());
    dispatch(closeVinDialog());
    dispatch(closeInitialPictureDialog());
  };

  const initialValues = useMemo(() => ({ images }), [images?.length]);

  const { isMobile } = useDeviceInfo();

  if (!vehicle || !images) {
    return null;
  }

  const nextAction = () => {
    if (!isMobile) {
      close();
      history.push(`/vehicles/${vehicleId}/review`);
      return;
    }

    if (type === 'upload') {
      close();
      dispatch(openQuestionsDialog({ vehicleId: vehicle.id, type }));
    } else {
      close();
      history.push(`/lister/upload-vehicle/v2/${vehicleId}`);
    }
  };

  const handleSubmit = async (values: typeof initialValues, form: any) => {
    submitCounter.current++;
    if (submitCounter.current === 1) {
      const [codes, smog, odometer] = values.images || [];
      if (!hasImage(codes) || !hasImage(smog) || !hasImage(odometer)) {
        return { [FORM_ERROR]: 'Please upload all photos' };
      }
    }

    const { dirty } = form.getState();
    if (!dirty) {
      return nextAction();
    }

    const [codes, smog, odometer] = values.images || [];

    dispatch(openLoadingModal());

    await saveObdImages({
      codes: codes.file,
      smog: smog.file,
      odometer: odometer.file,
      vehicleId: vehicle.id
    });

    dispatch(closeLoadingModal());

    return nextAction();
  };

  return (
    <StyledDialog classes={{ paper: 'create-vehicle-info-paper' }} open={!!vehicleId} onBackdropClick={close}>
      <Form
        mutators={{
          ...arrayMutators
        }}
        keepDirtyOnReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        {props => <RenderForm vehicleId={vehicleId} handleSubmit={props.handleSubmit} />}
      </Form>
    </StyledDialog>
  );
};

const ImageWrapper = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;

  & > div:last-child {
    transform: translateX(50%);
  }
`;

const RenderForm = ({ handleSubmit }: any) => {
  const { submitError } = useFormState();
  const { fields } = useFieldArray('images');

  const allSelected = fields.value.every(hasImage);

  const dispatch = useDispatch();

  const close = () => {
    dispatch(closeCarInfoDialog());
    dispatch(closeVinDialog());
    dispatch(closeInitialPictureDialog());
  };

  const uploadLabel = !allSelected && submitError ? 'Upload Later' : 'Uplaod';

  return (
    <Wrapper onSubmit={handleSubmit}>
      <LoadingDialog />
      <Header>
        <Title>Please Input Pictures</Title>
        <CloseIcon onClick={close}>close</CloseIcon>
      </Header>
      <ImageWrapper>
        <FieldArray name={`images`}>
          {({ fields }) =>
            fields.map((f, i) => (
              <Field name={f} key={f} render={props => <NoAutoSavePhoto {...props} key={props.input.name} />} />
            ))
          }
        </FieldArray>
      </ImageWrapper>
      {!allSelected && submitError ? <ErrorMessage>{submitError}</ErrorMessage> : <ErrorMessage />}
      <ActionsWrapper>
        <CancelAction fullWidth variant="contained" type="button" onClick={close}>
          Cancel
        </CancelAction>
        <SubmitAction fullWidth variant="contained" type="submit">
          {uploadLabel}
        </SubmitAction>
      </ActionsWrapper>
    </Wrapper>
  );
};
